import { Box, Typography } from '@mui/material';
import React from 'react';
import { StyledHeading } from '../../styles/styles';
import { FundDetailSelectField, FundDetailTextField } from './FundDetailFormFields';
import { FundDetailsCloseDateField } from './FundDetailsCloseDateField';
import type { FundDetailsSectionProps } from './FundDetailsSection';

type FieldProps = Pick<FundDetailsSectionProps, 'formData' | 'fieldErrors' | 'onInputChange'>;

function FinancialSelectFields({
  formData,
  onInputChange,
}: Pick<FieldProps, 'formData' | 'onInputChange'>) {
  return (
    <>
      <FundDetailSelectField
        label="Capital Type"
        value={formData.capitalType}
        onChange={(value) => onInputChange('capitalType', value)}
        options={[
          { value: 'SENIOR_DEBT', label: 'Senior Debt' },
          { value: 'JUNIOR_DEBT', label: 'Junior Debt' },
          { value: 'PREFERRED_EQUITY', label: 'Preferred Equity' },
        ]}
      />
      <FundDetailSelectField
        label="Interest Type"
        value={formData.interestType}
        onChange={(value) => onInputChange('interestType', value)}
        options={[
          { value: 'SERVICING_FULL', label: 'Servicing (Full)' },
          { value: 'SERVICING_PARTIAL', label: 'Servicing (Partial)' },
          { value: 'CAPITALISING', label: 'Capitalising' },
        ]}
      />
      <FundDetailSelectField
        label="Security"
        value={formData.security}
        onChange={(value) => onInputChange('security', value)}
        options={[
          { value: 'REGISTERED_MORTGAGE', label: 'Registered Mortgage' },
          { value: 'UNREGISTERED_MORTGAGE', label: 'Unregistered Mortgage' },
        ]}
      />
    </>
  );
}

export function FundFinancialFields({ formData, fieldErrors, onInputChange }: FieldProps) {
  return (
    <>
      <StyledHeading headerVariant="subheading" sx={{ mt: 3 }}>
        Financials
      </StyledHeading>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }}
        gap={2}
        sx={{ mt: 1 }}
      >
        <FundDetailTextField
          label="Interest Rate"
          name="interest"
          value={formData.interest ?? ''}
          onChange={(value) => onInputChange('interest', value)}
          error={fieldErrors?.interest}
          number
          endAdornment="%"
        />
        <FinancialSelectFields formData={formData} onInputChange={onInputChange} />
        <FundDetailTextField
          label="Facility Limit"
          name="facilityLimit"
          value={formData.facilityLimit ?? ''}
          onChange={(value) => onInputChange('facilityLimit', value)}
          error={fieldErrors?.facilityLimit}
          number
          startAdornment="$"
        />
        <FundDetailTextField
          label="Senior Debt LVR"
          name="seniorDebtLvr"
          value={formData.seniorDebtLvr ?? ''}
          onChange={(value) => onInputChange('seniorDebtLvr', value)}
          error={fieldErrors?.seniorDebtLvr}
          number
          endAdornment="%"
        />
        <FundDetailTextField
          label="Current Valuation"
          name="currentValuation"
          value={formData.currentValuation ?? ''}
          onChange={(value) => onInputChange('currentValuation', value)}
          error={fieldErrors?.currentValuation}
          number
          startAdornment="$"
        />
      </Box>
    </>
  );
}

export function FundSiteAndDatesFields({ formData, fieldErrors, onInputChange }: FieldProps) {
  return (
    <>
      <StyledHeading headerVariant="subheading" sx={{ mt: 3 }}>
        Site &amp; Dates
      </StyledHeading>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }}
        gap={2}
        sx={{ mt: 1 }}
      >
        <FundDetailTextField
          label="LGA"
          name="lga"
          value={formData.lga ?? ''}
          onChange={(value) => onInputChange('lga', value)}
          error={fieldErrors?.lga}
        />
        <FundDetailTextField
          label="Site Area"
          name="siteArea"
          value={formData.siteArea ?? ''}
          onChange={(value) => onInputChange('siteArea', value)}
          error={fieldErrors?.siteArea}
          number
          endAdornment="sqm"
        />
        <FundDetailsCloseDateField
          value={formData.closeDate}
          error={fieldErrors?.closeDate}
          onChange={(value) => onInputChange('closeDate', value)}
        />
        <FundDetailTextField
          label="Country"
          name="country"
          value={formData.country ?? ''}
          onChange={() => undefined}
          readOnly
        />
        <FundDetailTextField
          label="State"
          name="state"
          value={formData.state ?? ''}
          onChange={() => undefined}
          readOnly
        />
      </Box>
      <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
        Country and State will be populated automatically when you add the first address.
      </Typography>
    </>
  );
}
